export interface SpotlightFrame {
  top: number;
  left: number;
  width: number;
  height: number;
  borderRadius: string;
}

export interface SpotlightMaskRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rx: string;
  ry: string;
}

export interface SpotlightGeometry {
  targetRect: DOMRect;
  frame: SpotlightFrame;
  mask: SpotlightMaskRect;
}

const INTERACTIVE_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'textarea',
  'select',
  '[role="button"]',
  '[role="tab"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[data-radix-collection-item]',
  '[data-tour]',
].join(',');

function hasVisibleRect(rect: DOMRect) {
  return rect.width > 0 && rect.height > 0;
}

function overlaps(aStart: number, aEnd: number, bStart: number, bEnd: number) {
  return Math.min(aEnd, bEnd) - Math.max(aStart, bStart) > 4;
}

function clampPadding(basePadding: number, gap: number) {
  if (!Number.isFinite(gap)) return basePadding;
  return Math.max(0, Math.min(basePadding, Math.floor(Math.max(0, gap / 2 - 1))));
}

function getBasePadding(target: HTMLElement, rect: DOMRect) {
  const tag = target.tagName.toLowerCase();
  const role = target.getAttribute('role');
  const isCompactControl = rect.height <= 56 && (
    tag === 'button' ||
    tag === 'a' ||
    tag === 'input' ||
    tag === 'select' ||
    role === 'button' ||
    role === 'tab' ||
    role === 'switch' ||
    role === 'checkbox' ||
    role === 'radio'
  );

  if (isCompactControl) return 3;
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return 4;
  if (rect.height <= 72) return 4;
  return 6;
}

function getNearestGap(target: HTMLElement, targetRect: DOMRect, side: 'top' | 'right' | 'bottom' | 'left') {
  let minGap = Number.POSITIVE_INFINITY;

  document.querySelectorAll<HTMLElement>(INTERACTIVE_SELECTOR).forEach((candidate) => {
    if (candidate === target || target.contains(candidate) || candidate.contains(target)) return;

    const styles = window.getComputedStyle(candidate);
    if (styles.display === 'none' || styles.visibility === 'hidden') return;

    const rect = candidate.getBoundingClientRect();
    if (!hasVisibleRect(rect)) return;

    if (side === 'left') {
      if (!overlaps(rect.top, rect.bottom, targetRect.top, targetRect.bottom)) return;
      const gap = targetRect.left - rect.right;
      if (gap >= 0) minGap = Math.min(minGap, gap);
      return;
    }

    if (side === 'right') {
      if (!overlaps(rect.top, rect.bottom, targetRect.top, targetRect.bottom)) return;
      const gap = rect.left - targetRect.right;
      if (gap >= 0) minGap = Math.min(minGap, gap);
      return;
    }

    if (side === 'top') {
      if (!overlaps(rect.left, rect.right, targetRect.left, targetRect.right)) return;
      const gap = targetRect.top - rect.bottom;
      if (gap >= 0) minGap = Math.min(minGap, gap);
      return;
    }

    if (!overlaps(rect.left, rect.right, targetRect.left, targetRect.right)) return;
    const gap = rect.top - targetRect.bottom;
    if (gap >= 0) minGap = Math.min(minGap, gap);
  });

  return minGap;
}

function expandBorderRadius(borderRadius: string, extraPadding: number) {
  const normalized = borderRadius.trim();
  if (!normalized) return `${8 + extraPadding}px`;

  if (normalized.includes('px')) {
    return normalized
      .split('/')
      .map((group) =>
        group
          .trim()
          .split(/\s+/)
          .map((token) => {
            const value = Number.parseFloat(token);
            return Number.isFinite(value) ? `${Math.max(0, value + extraPadding)}px` : token;
          })
          .join(' '),
      )
      .join(' / ');
  }

  const value = Number.parseFloat(normalized);
  if (Number.isFinite(value) && !normalized.includes('%')) {
    return `${Math.max(0, value + extraPadding)}px`;
  }

  return normalized;
}

export function shouldScrollTargetIntoView(targetRect: DOMRect) {
  const viewportPadding = 72;
  return targetRect.top < viewportPadding || targetRect.bottom > window.innerHeight - viewportPadding;
}

/**
 * Cached metrics that are expensive to compute (neighbour scanning, style reads)
 * but stable while the element stays the same size / position relative to siblings.
 */
export interface SpotlightCachedMetrics {
  uniformPadding: number;
  borderRadius: string;
}

/** Compute the expensive padding + border-radius once. */
export function computeCachedMetrics(target: HTMLElement): SpotlightCachedMetrics {
  const targetRect = target.getBoundingClientRect();
  const basePadding = getBasePadding(target, targetRect);

  const perSide = {
    top: clampPadding(basePadding, getNearestGap(target, targetRect, 'top')),
    right: clampPadding(basePadding, getNearestGap(target, targetRect, 'right')),
    bottom: clampPadding(basePadding, getNearestGap(target, targetRect, 'bottom')),
    left: clampPadding(basePadding, getNearestGap(target, targetRect, 'left')),
  };
  const uniformPadding = Math.min(perSide.top, perSide.right, perSide.bottom, perSide.left);
  const borderRadius = expandBorderRadius(window.getComputedStyle(target).borderRadius || '8px', uniformPadding);

  return { uniformPadding, borderRadius };
}

/** Cheap per-frame projection: just reads getBoundingClientRect and applies cached offsets. */
export function projectSpotlight(target: HTMLElement, cached: SpotlightCachedMetrics): SpotlightGeometry {
  const targetRect = target.getBoundingClientRect();
  const { uniformPadding, borderRadius } = cached;

  const left = targetRect.left - uniformPadding;
  const top = targetRect.top - uniformPadding;
  const width = targetRect.width + uniformPadding * 2;
  const height = targetRect.height + uniformPadding * 2;

  return {
    targetRect,
    frame: { top, left, width, height, borderRadius },
    mask: { x: left, y: top, width, height, rx: borderRadius, ry: borderRadius },
  };
}

export function getSpotlightGeometry(target: HTMLElement): SpotlightGeometry {
  const cached = computeCachedMetrics(target);
  return projectSpotlight(target, cached);
}